import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import {
  autoEligibilityTime,
  eligibilityCoveredMembers,
  EligibilityCoveredMembersListResponse,
  insurancePayerPlans,
  InsurancePayerPlansListResponse,
  insuranceProviderPayer,
  ProvidersUserPeopleRetrieveResponse,
  userCoverageCheckEligibility,
  userCoverageStatuses,
  UserCoverageStatusesListResponse,
} from '@fireflyhealth/api'
import { useMutation } from '~/shared/hooks/promiseState'
import {
  Button,
  Grid,
  Typography,
  Box,
  Switch,
  Avatar,
  IconButton,
  type TextFieldProps,
} from '@mui/material'
import { useFormik } from 'formik'
import omit from 'lodash/fp/omit'
import orderBy from 'lodash/orderBy'

import { EditField } from '~/shared/ui'
import EditPatientInsuranceCard from './EditPatientInsuranceCard'
import InsuranceContractInfo from './InsuranceContractInfo'
import EligibilityCheckResponseDetails from './EligibilityCheckResponseDetails'
import { difference } from './utils'
import { getUserAge } from '~/shared/lib/date'
import { useQuery } from '~/shared/hooks/query'
import { useNavigate } from 'react-router'
import LaunchIcon from '@mui/icons-material/Launch'
import {
  useCreateOrUpdateInsurance,
  useGetPersonInsurance,
  useUpdateInsuranceCoverage,
} from './PersonInsuranceService'
import { patientsSlice } from '~/entities/person'
import { queueNotification } from '~/shared/notifications/actions'
import { useAppDispatch } from '~/app/store/hooks'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { logger } from '~/shared/lib/logger'

interface EditInsuranceFormProps {
  /**
   * The full person snapshot the form renders against. Used for fields the BFF
   * insurance row does not carry (e.g. `person.coverage`, addresses, payer).
   */
  person: ProvidersUserPeopleRetrieveResponse
  /**
   * Parent `User.id`. When provided, the eligibility check button renders, the
   * coverage-status update path is enabled, and successful saves trigger a
   * Redux refresh of the patient row so the other EditPatient tabs see the
   * canonical post-write shape. Undefined in person-only flows (EditPerson).
   */
  userId?: number | null
}

type CoveredMember = EligibilityCoveredMembersListResponse[number]

// Narrow value shape Formik drives this form against. The form is person-centric
// (every field it edits hangs off `person`/`person.insurance_info`); the parent
// `User.id` is carried separately as `id` and is the only `UserRetrieveResponse`
// field the form needs. Using this instead of `PatientUser` (which requires the
// full `UserRetrieveResponse` surface) keeps the synthesized shell honest with
// what is actually built below and removes the need for an `as unknown` cast.
type InsuranceFormValues = {
  /** Parent `User.id`; absent in the person-only flow (EditPerson). */
  id: number | undefined
  person: ProvidersUserPeopleRetrieveResponse
}

const useCoveredMembers = (personId: number) =>
  useQuery(
    eligibilityCoveredMembers.listQueryKey({ person_id: personId }),
    async () => (await eligibilityCoveredMembers.list({ params: { person_id: personId } })).data,
    {},
    { error: `Failed to fetch covered members for ${personId}` },
  )

const useInsurancePayers = () =>
  useQuery(
    insuranceProviderPayer.listQueryKey(),
    async () => (await insuranceProviderPayer.list()).data,
    { staleTime: Infinity },
    { error: 'Failed to fetch Insurance Payers' },
  )

// `enabled` is gated on `patientId` so the person-only flow (userId undefined)
// does not fire this request.
const useEligibilityData = (patientId: number | null | undefined) =>
  useQuery(
    autoEligibilityTime.retrieveQueryKey({ patient_id: patientId as number }),
    async () => (await autoEligibilityTime.retrieve({ patient_id: patientId as number })).data,
    { enabled: patientId != null, staleTime: Infinity },
    { error: 'Failed to load eligibility' },
  )

/**
 * Self-contained insurance editor used by both EditPatient (patient-side, with
 * `userId`) and EditPerson (person-only, no `userId`).
 *
 * Loader/action pattern:
 *   Loader → useGetPersonInsurance (GET /bff/crm/person/{id}/insurance/)
 *            returns the `InsuranceMemberInfoWithNetworkSerializer` shape
 *            (includes `insurance_plan.networks`).
 *   Action → useCreateOrUpdateInsurance (POST /bff/crm/person/{id}/insurance/)
 *            invalidates the loader query and the person retrieve query on
 *            success so EditPerson's parent `useGetPerson` reseeds Formik via
 *            `enableReinitialize`. EditPatient additionally dispatches
 *            `patientsSlice.thunks.getPatient(userId)` below to keep the
 *            Redux-backed sibling tabs in sync.
 *
 * The eligibility-check button and the coverage-status mutation are gated on
 * `userId`; the person-only flow has no parent `User` row to drive them.
 */
const fetchCoverageStatuses = () => userCoverageStatuses.list().then(r => r.data)

const EditInsuranceForm: FC<EditInsuranceFormProps> = ({ person, userId }) => {
  const [isPlanSelectionDisabled, setIsPlanSelectionDisabled] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const dispatch = useAppDispatch()

  // BFF loader. Skip when the person has no insurance row — the BFF responds
  // 404 and the error would surface as a toast on initial render.
  const { data: insuranceData } = useGetPersonInsurance(
    { personId: person.id },
    { enabled: person.insurance_info != null },
  )

  // Render the form read-only when the person's insurance is owned by an
  // upstream flow the CRM must not override: an EligibilityRecord feed (linked
  // and actively enrolled in benefit), or a member still mid-onboarding
  // (onboarding status INITIALIZED). The BFF rejects the writes in both cases
  // (`BlockEligibilitySourcedFieldsIfLinked` /
  // `BlockInsuranceEditsIfOnboardingInitialized`); disabling the fields and
  // hiding Save gives immediate UX feedback.
  const isReadOnly =
    insuranceData?.is_eligibility_locked === true || insuranceData?.is_onboarding_locked === true

  const { mutateAsync: handleCreateOrUpdateInsurance } = useCreateOrUpdateInsurance()
  const { mutateAsync: handleUpdateInsuranceCoverage } = useUpdateInsuranceCoverage()

  const { handler: loadCoverageStatuses, result: coverageStatusesResult } = useMutation<
    [void],
    UserCoverageStatusesListResponse
  >(fetchCoverageStatuses)
  useEffect(() => {
    void loadCoverageStatuses()
  }, [loadCoverageStatuses])
  // Memoized so the empty-list fallback does not hand the submit callbacks below a
  // new array identity on every render.
  const existingCoverage = useMemo(() => coverageStatusesResult ?? [], [coverageStatusesResult])

  const { data: lastEligibilityResult, refetch: refetchEligibilityData } =
    useEligibilityData(userId)
  const lastEligibilityCheckData = lastEligibilityResult ?? null

  // Synthesize the form-values shell the existing JSX consumes. When the BFF
  // loader has resolved, overlay its richer insurance shape (with
  // `insurance_plan.networks`) onto the parent person snapshot.
  // `BffCrmPersonInsuranceRetrieveResponse` and
  // `ProvidersUserPeopleRetrieveResponse['insurance_info']` are structurally
  // identical (both serialize `InsuranceMemberInfoWithNetworkSerializer`), so
  // the overlay assigns cleanly without a cast.
  const patient = useMemo<InsuranceFormValues>(() => {
    const insurance_info: ProvidersUserPeopleRetrieveResponse['insurance_info'] =
      insuranceData ?? person.insurance_info
    return {
      id: userId ?? undefined,
      person: { ...person, insurance_info },
    }
  }, [person, insuranceData, userId])

  const handleInsuranceSubmit = useCallback(
    async (values: InsuranceFormValues) => {
      const insuranceInfo = values.person.insurance_info
      if (insuranceInfo?.id == null) return
      // Child form component chooses coverage by name, so only POST full object
      const coverage = existingCoverage.find(c => c.name === values.person.coverage?.name)
      try {
        setLoading(true)
        // Diff full values against the synthesized shell (BFF insurance is the
        // source of truth post-load). Extract just the insurance_info branch.
        const updatedValues = difference(values, patient)
        const insuranceBranch = updatedValues.person?.insurance_info ?? null
        await handleCreateOrUpdateInsurance({
          personId: person.id,
          // Omit fields owned by sibling flows: image_front/back is the upload
          // form; addresses is handled by EditAddressForm via the legacy
          // /insurance/member/{id} PATCH.
          ...omit(['image_front', 'image_back', 'addresses'], insuranceBranch),
        })
        if (userId != null && coverage && coverage.status !== person.coverage?.status) {
          await handleUpdateInsuranceCoverage({ patientId: userId, coverage })
        }
        if (userId != null) {
          // Refresh Redux so the EditPatient sibling tabs (Membership, Address,
          // BasicInfo, CareTeam) see the canonical post-write shape.
          await dispatch(patientsSlice.thunks.getPatient(userId))
        }
      } catch (e) {
        logger.error('[Patient] save insurance', e)
      } finally {
        setLoading(false)
      }
    },
    [
      person,
      patient,
      existingCoverage,
      userId,
      dispatch,
      handleCreateOrUpdateInsurance,
      handleUpdateInsuranceCoverage,
    ],
  )

  const handleEligibilityCheck = useCallback(
    async (values: InsuranceFormValues) => {
      if (userId == null) return
      const insuranceInfo = values.person.insurance_info
      // The backend only exposes PATCH /insurance/member/{id}; without an
      // existing member id there is no row to update.
      if (insuranceInfo?.id == null) return
      try {
        setLoading(true)
        // Save any insurance changes before submitting eligibility
        await handleInsuranceSubmit(values)
        const updatedValues = difference(values, patient)
        const insuranceBranch = updatedValues.person?.insurance_info ?? null
        await handleCreateOrUpdateInsurance({
          personId: person.id,
          ...omit(['image_front', 'image_back'], insuranceBranch),
        })
        // Backend `check_insurance_eligibility` ignores the request body and
        // re-checks against the user's saved insurance info, so send an
        // empty payload rather than the legacy coverage-status object.
        await userCoverageCheckEligibility.create({ id: userId }, { data: {} })
        await refetchEligibilityData()

        fireAndForget('[Patient] refetch patient', () =>
          dispatch(patientsSlice.thunks.getPatient(userId)),
        )
        dispatch(
          queueNotification({
            variant: 'success',
            message: 'Patient insurance eligibility updated',
          }),
        )
      } catch (e: unknown) {
        logger.error('[Patient] update insurance eligibility', e)
        const message =
          typeof e === 'object' && e !== null && 'data' in e
            ? ((e as { data?: { error?: string } }).data?.error ?? 'An error occurred')
            : 'An error occurred'
        dispatch(
          queueNotification({
            variant: 'error',
            message,
          }),
        )
      } finally {
        setLoading(false)
      }
    },
    [
      userId,
      person,
      patient,
      handleInsuranceSubmit,
      refetchEligibilityData,
      dispatch,
      handleCreateOrUpdateInsurance,
    ],
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: patient,
    onSubmit: async values => {
      await handleInsuranceSubmit(values)
      setIsPlanSelectionDisabled(false)
    },
  })

  const {
    person: { insurance_info: insuranceInfo, coverage },
  } = formik.values

  const { data: coveredMembers } = useCoveredMembers(patient.person.id)
  const [plans, setPlans] = useState<InsurancePayerPlansListResponse | null>([])
  const payers = useInsurancePayers()
  const insurancePayerId = insuranceInfo?.insurance_payer?.id
  useEffect(() => {
    if (isPlanSelectionDisabled || insurancePayerId == null) {
      return
    }
    const fetchPlans = async () => {
      const { data } = await insurancePayerPlans.list({
        insurance_payer_id: Number(insurancePayerId),
      })
      const sortedPlans = orderBy(data, plan => plan.name, ['asc'])
      setPlans(sortedPlans)
    }
    fireAndForget('[Insurance] load plans', fetchPlans)
  }, [insurancePayerId, isPlanSelectionDisabled])

  // Shortcut for change handlers and values.
  // Also disables the field when the insurance record is read-only (see
  // `isReadOnly` above) so the upstream flow owns the data. The BFF would
  // reject the write anyway; disabling gives immediate UX feedback.
  const setValues = (name: string, value: TextFieldProps['value']) => ({
    name,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      e.persist()
      formik.handleChange(e)
      void formik.setFieldTouched(name, true, false)
    },
    disabled: loading || isReadOnly,
    value,
  })

  const disableInsurancePlanSelector = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist()
    formik.handleChange(e)
    void formik.setFieldTouched('person.insurance_info.insurance_payer_id', true, true)
    void formik.setFieldValue('person.insurance_info.insurance_payer.id', e.target.value)
    void formik.setFieldValue('person.insurance_info.insurance_plan_id', '')
    setIsPlanSelectionDisabled(true)
  }

  // Accepts the snake-case insurance_plan.networks shape from
  // UserRetrieveResponse (post-PR 10 dual shim); `name` is optional on the
  // DeepPartial side, so widen the parameter to match the runtime data.
  const getNetworksNames = (networks: Array<{ name?: string | null }>) => {
    return networks
      ?.map(network => {
        return network?.name
      })
      .join(', ')
  }

  const navigate = useNavigate()
  const handleNavigation = (member: CoveredMember) => {
    if (member.user) void navigate(`/patients/${member.user}`)
    else void navigate(`/people/${member.member_person_id}`)
  }
  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ mt: 3, mb: 1 }}>
        <EditPatientInsuranceCard patient={patient} />
      </Box>
      <Box sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Insurance Info</Typography>
        </Box>
      </Box>
      {lastEligibilityCheckData && (
        <Box sx={{ mb: 3 }}>
          <EligibilityCheckResponseDetails responseData={lastEligibilityCheckData} />
        </Box>
      )}
      <Grid container spacing={2}>
        {existingCoverage.length && (
          <EditField
            {...setValues('person.coverage.name', coverage.name)}
            label="Status"
            select
            slotProps={{ select: { native: true } }}
          >
            {existingCoverage.map(v => (
              <option key={v.name} value={v.name}>
                {v.name}
              </option>
            ))}
          </EditField>
        )}
        <EditField
          {...setValues('person.insurance_info.member_id', insuranceInfo?.member_id)}
          label="Member ID"
        />
        <EditField
          {...setValues(
            'person.insurance_info.insurance_payer_id',
            insuranceInfo?.insurance_payer?.id,
          )}
          label="Insurance Payer"
          select
          onChange={disableInsurancePlanSelector}
          slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
        >
          <option value="">none</option>
          {(payers?.data ?? []).map(payer => (
            <option key={payer.id} value={payer.id}>
              {payer.name}
            </option>
          ))}
        </EditField>
        <Grid key="insurance-plan-field" size={6}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', flexGrow: 1 }}>
            <EditField
              {...setValues(
                'person.insurance_info.insurance_plan_id',
                insuranceInfo?.insurance_plan_id,
              )}
              label="Insurance Plan"
              select
              disabled={isPlanSelectionDisabled || loading || isReadOnly}
              gridSizeOverride={11}
              slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
            >
              <option value="">none</option>
              {plans?.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </EditField>
            {insuranceInfo?.insurance_plan_id ? (
              <IconButton
                component="a"
                href={`${import.meta.env.VITE_ADMIN_URL}insurance/coverageproduct/${
                  insuranceInfo?.insurance_plan_id
                }/change/`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                title="Open in Django Admin"
              >
                <LaunchIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        </Grid>
        <EditField
          {...setValues('person.insurance_info.group_number', insuranceInfo?.group_number)}
          label="Group Number"
        />
        <EditField
          label="Plan Type"
          name="person.insurance_info.product_type"
          value={insuranceInfo?.product_type?.toUpperCase() ?? ''}
          onChange={() => {}}
          disabled={true}
        />
        <EditField
          {...setValues(
            'person.insurance_info.coverage_start',
            insuranceInfo?.coverage_start ?? ' ',
          )}
          label="Valid Date"
          type="date"
          disabled={loading || isReadOnly}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <EditField
          {...setValues('person.insurance_info.coverage_end', insuranceInfo?.coverage_end ?? ' ')}
          label="Expired Date"
          type="date"
          disabled={loading || isReadOnly}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <EditField
          {...setValues('person.insurance_info.pcp_name', insuranceInfo?.pcp_name ?? ' ')}
          label="PCP"
          disabled={true}
        />
        <EditField
          label="Insurance Plan Networks"
          type="text"
          value={getNetworksNames(insuranceInfo?.insurance_plan?.networks ?? [])}
          disabled={true}
          multiline
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Grid>
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Switch
            {...setValues(
              'person.insurance_info.is_primary_subscriber',
              insuranceInfo?.is_primary_subscriber,
            )}
            edge="start"
            checked={!!formik.values.person.insurance_info?.is_primary_subscriber}
            color="primary"
          />
          <Box sx={{ ml: 1 }}>
            <Typography variant="body2">Patient is Primary Subscriber</Typography>
          </Box>
        </Box>
        {formik.values.person.insurance_info?.is_primary_subscriber === false && (
          <Grid container spacing={2}>
            <EditField
              {...setValues(
                'person.insurance_info.primary_first_name',
                insuranceInfo?.primary_first_name,
              )}
              label="Subscriber First Name"
            />
            <EditField
              {...setValues(
                'person.insurance_info.primary_last_name',
                insuranceInfo?.primary_last_name,
              )}
              label="Subscriber Last Name"
            />
            <EditField
              {...setValues('person.insurance_info.primary_dob', insuranceInfo?.primary_dob)}
              label="Subscriber Date of Birth"
              type="date"
            />
          </Grid>
        )}
      </Box>

      <Box sx={{ my: 2 }}>
        {formik.values.person.insurance_info ? (
          <InsuranceContractInfo insuranceInfo={formik.values.person.insurance_info} />
        ) : null}
      </Box>
      {coveredMembers && coveredMembers.length > 0 && (
        <>
          <Box sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2">Covered Members ({coveredMembers?.length})</Typography>
            </Box>
          </Box>
          <Box sx={{ my: 2 }}>
            <Grid container spacing={3}>
              {coveredMembers?.map(member => {
                return (
                  <Grid key={member.member_person_id} size={{ xs: 5, md: 4 }}>
                    <Box sx={{ display: 'flex', flex: '1', alignItems: 'center' }}>
                      <Box sx={{ mr: 1, alignSelf: 'baseline' }}>
                        <Avatar
                          sx={{
                            textTransform: 'uppercase',
                            margin: '1rem',
                            fontSize: '1.5rem',
                            width: '4.5rem',
                            height: '4.5rem',
                            bgcolor: 'grey.50',
                            color: 'text.primary',
                          }}
                          style={{ margin: 0, display: 'inline-flex' }}
                        >
                          {(member.first_name ?? '').slice(0, 1) +
                            (member.last_name ?? '').slice(0, 1)}
                        </Avatar>
                      </Box>
                      <div>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Button
                            style={{ padding: 0 }}
                            onClick={() => {
                              handleNavigation(member)
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'primary.main' }}
                              style={{ fontWeight: 'bold' }}
                            >
                              {member.first_name + ' ' + member.last_name}
                            </Typography>
                          </Button>
                        </Box>
                        <Box sx={{ color: 'text.primary' }}>
                          <Typography variant="caption">{member.relationship}</Typography>
                          {getUserAge(member.member_dob) < 18 && (
                            <Typography variant="caption">{' <18'}</Typography>
                          )}
                        </Box>
                        {member.is_primary_subscriber && (
                          <Box sx={{ color: 'black', fontStyle: 'italic', fontSize: '1.25rem' }}>
                            {'Primary Subscriber'}
                          </Box>
                        )}
                      </div>
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          </Box>
        </>
      )}
      <Grid container spacing={1} sx={{ justifyContent: 'flex-end' }}>
        {formik.dirty && (
          <Grid>
            <Button onClick={() => formik.resetForm()}>Cancel Insurance Changes</Button>
          </Grid>
        )}
        {!isReadOnly && (
          <Grid>
            <Button type="submit" color="primary" variant="outlined">
              Save Insurance
            </Button>
          </Grid>
        )}
        {patient.id && !isReadOnly && (
          <Grid>
            <Button
              onClick={() =>
                void handleEligibilityCheck(formik.values).then(() =>
                  setIsPlanSelectionDisabled(false),
                )
              }
              color="primary"
              variant="outlined"
            >
              Save and Check Eligibility
            </Button>
          </Grid>
        )}
      </Grid>
    </form>
  )
}

export default EditInsuranceForm
